import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  useMediaQuery,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuOptionGroup,
  Portal,
  Image,
  Input,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { ChevronDownIcon } from '@chakra-ui/icons';
import Dropzone from 'react-dropzone';
import moment from 'moment';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { BsArrowRight } from 'react-icons/bs';

import UpdateBlockSchema from 'validation/schemas/UpdateBlockSchema';
import { handleFileDelete, handleFileUpload } from 'services/s3Service';
import FullScreenLoader from 'components/FullScreenLoader';
import { acceptedFormats } from 'utils/FileFormats';
import { CURRENCY_OPTIONS, MONTH_NAMES } from 'projectConstants';
import { UpdateModelProperties, FetchModelBlocks, GetUserModels } from 'redux/ModelsSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import DatePickerIcon from 'components/Icons/DatePickerIcon';
import { accent, font, fontSize, neutral, radius, shadow, space, text } from 'styles/revampTokens';

import ModelSettingsDatePicker from './ModelSettingsDatePicker';

/** Match /model/{id}/time: send Jan–Dec abbrev, never 1–12. */
function fyStartMonthToApiAbbrev(value: string | undefined | null): string {
  if (value == null || String(value).trim() === '') {
    return 'Jan';
  }
  const s = String(value).trim();
  const match = MONTH_NAMES.find((m) => s === m || s.toLowerCase().startsWith(m.toLowerCase()));
  if (match) return match;
  const n = Number.parseInt(s, 10);
  if (!Number.isNaN(n) && n >= 1 && n <= 12) {
    return MONTH_NAMES[n - 1];
  }
  return 'Jan';
}

function getOwnerDisplay(modelData: Record<string, unknown> | undefined): {
  name: string;
  image?: string;
} {
  if (!modelData) return { name: 'N/A' };
  const md = modelData as {
    ownerName?: string;
    ownerImage?: string;
    creator?: string;
    owner?: {
      firstname?: string;
      lastname?: string;
      profile_image_url?: string;
      username?: string;
    };
  };
  if (md.ownerName) {
    return { name: md.ownerName, image: md.ownerImage };
  }
  const o = md.owner;
  if (o && typeof o === 'object') {
    const full = [o.firstname, o.lastname].filter(Boolean).join(' ').trim();
    return {
      name: full || o.username || md.creator || 'N/A',
      image: o.profile_image_url ?? md.ownerImage,
    };
  }
  return { name: md.creator || 'N/A', image: md.ownerImage };
}

interface ModelSettingsModalProperties {
  isOpen: boolean;
  onClose: () => void;
  modelData?: {
    name?: string;
    description?: string;
    bannerImage?: string;
    banner_image?: string;
    icon?: string;
    startDate?: Date;
    endDate?: Date;
    currency?: string;
    reporting_currency?: string;
    createdAt?: string;
    createdDate?: string;
    date_created?: string;
    ownerName?: string;
    ownerImage?: string;
    owner?: {
      firstname?: string;
      lastname?: string;
      profile_image_url?: string;
      username?: string;
    };
    creator?: string;
    time_granularity?: 'M' | 'Y';
    plan_duration_periods?: number;
    starting_period?: string;
    ending_period?: string;
    fy_start_month?: string;
    time_properties?: {
      time_granularity?: 'M' | 'Y';
      plan_duration_periods?: number;
      starting_period?: string;
      ending_period?: string;
      fy_start_month?: string;
    };
  };
  onSave?: (form: any) => void;
}

const ModelSettingsModal = ({
  isOpen,
  onClose,
  modelData,
  onSave,
}: ModelSettingsModalProperties) => {
  const { modelId } = useParams();
  const dispatch: any = useDispatch();
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 575px)');
  const [bannerLoader, setBannerLoader] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState(CURRENCY_OPTIONS[5]);
  const ownerDisplay = getOwnerDisplay(modelData as Record<string, unknown> | undefined);
  const timeProperties = modelData?.time_properties || modelData;

  useEffect(() => {
    const md = modelData as ModelSettingsModalProperties['modelData'] | undefined;
    setBannerImage(md?.bannerImage ?? md?.banner_image ?? null);
    const currencyCode = md?.currency ?? md?.reporting_currency;
    const nextCurrency =
      (currencyCode && CURRENCY_OPTIONS.find((c) => c.value === currencyCode)) ||
      CURRENCY_OPTIONS[5];
    setSelectedCurrency(nextCurrency);
  }, [modelData]);

  const [timescale, setTimescale] = useState<'M' | 'Y'>(
    (timeProperties?.time_granularity?.toUpperCase() as 'M' | 'Y') || 'M',
  );
  const [periodAmount, setPeriodAmount] = useState(
    timeProperties?.plan_duration_periods?.toString() ||
      (timeProperties?.time_granularity?.toLowerCase() === 'm' ? '36' : '3'),
  );
  const [maxValue, setMaxValue] = useState(36);
  const [fyStartMonth, setFyStartMonth] = useState(() =>
    fyStartMonthToApiAbbrev(timeProperties?.fy_start_month),
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const timeGranualityLimit =
    UserSubscriptionLimitData?.subscription_features_limit?.model_timescale;

  // Helper function to parse date from period string
  const parseDateFromPeriod = (period: string, granularity: string): Date => {
    if (granularity === 'Y') {
      // Format: "YYYY"
      const year = Number.parseInt(period, 10);
      return new Date(year, 0, 1);
    }
    // Format: "DD MMM YY" or ISO string
    const parsed = new Date(period);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
    // Try parsing "DD MMM YY" format
    const parts = period.split(' ');
    if (parts.length === 3) {
      const day = Number.parseInt(parts[0], 10);
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames.indexOf(parts[1]);
      const year = 2000 + Number.parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return new Date();
  };

  useEffect(() => {
    if (
      timeGranualityLimit === null &&
      UserSubscriptionLimitData?.current_subscription_name !== 'Ultimate'
    ) {
      setMaxValue(-1);
    } else {
      if (timescale.toLowerCase() === 'm') {
        setMaxValue(timeGranualityLimit);
      }
      if (timescale.toLowerCase() === 'y') {
        setMaxValue(timeGranualityLimit / 12);
      }
    }
  }, [timescale, UserSubscriptionLimitData, timeGranualityLimit]);

  // Parse dates from time_properties
  useEffect(() => {
    if (timeProperties) {
      const timeGranularity = timeProperties?.time_granularity?.toUpperCase() || 'M';
      setTimescale(timeGranularity as 'M' | 'Y');
      const value = Number.isNaN(timeProperties?.plan_duration_periods)
        ? timeGranularity === 'M'
          ? 36
          : 3
        : Number(timeProperties?.plan_duration_periods);
      setPeriodAmount(value.toString());
      setFyStartMonth(fyStartMonthToApiAbbrev(timeProperties?.fy_start_month));

      // Parse starting_period
      if (timeProperties?.starting_period) {
        const startDateParsed = parseDateFromPeriod(
          timeProperties.starting_period,
          timeGranularity,
        );
        setStartDate(startDateParsed);
      }

      // Parse ending_period or calculate from starting_period
      if (timeProperties?.ending_period) {
        const endDateParsed = parseDateFromPeriod(timeProperties.ending_period, timeGranularity);
        setEndDate(endDateParsed);
      } else if (timeProperties?.starting_period) {
        const startDateParsed = parseDateFromPeriod(
          timeProperties.starting_period,
          timeGranularity,
        );
        const endDateCalc = new Date(startDateParsed);
        const targetValue = timeGranularity === 'M' ? value : value * 12;
        const dateEnd = endDateCalc.setMonth(endDateCalc.getMonth() + targetValue);
        setEndDate(new Date(dateEnd));
      }
    }
  }, [timeProperties]);

  const formatDate = (date: string) => {
    return moment(date).format('DD MMM YYYY');
  };

  // const changeTimeInput = useCallback(
  //   (time: string, amount: string, startDateValue: Date | null, endDateValue?: Date | null) => {
  //     if (!modelId) return;

  //     const requestObject: any = {
  //       model_id: Number(modelId),
  //       request_body: {
  //         time_granularity: time,
  //         plan_duration_periods: Number(amount),
  //         fy_start_month: fyStartMonth,
  //       },
  //     };
  //     if (endDateValue) {
  //       const endInDate = endDateValue.toDateString();
  //       requestObject.request_body.ending_period =
  //         time === 'Y' ? dayjs(endInDate).format('YYYY') : dayjs(endInDate).format('DD MMM YY');
  //     }
  //     if (startDateValue !== null) {
  //       const startInDate = startDateValue.toDateString();
  //       requestObject.request_body.starting_period =
  //         time === 'Y' ? dayjs(startInDate).format('YYYY') : dayjs(startInDate).format('DD MMM YY');
  //     }
  //     dispatch(UpdateModelTime({ requestObject, toaster: true }));
  //   },
  //   [modelId, dispatch, fyStartMonth],
  // );

  const onChangePeriod = (valueDiff: any) => {
    const newValue = valueDiff;
    if (Number(newValue) <= maxValue) {
      setPeriodAmount(newValue.toString());
    }
    if (maxValue === -1) {
      setPeriodAmount(newValue.toString());
    }
  };

  const startDateChange = (startDateValue: Date) => {
    if (!endDate) return '0';
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDate.getFullYear() - startDateValue.getFullYear()) * 12 +
        (endDate.getMonth() - startDateValue.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDate.getFullYear() - startDateValue.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const endDateChange = (endDateValue: Date) => {
    if (!startDate) return '0';
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDateValue.getFullYear() - startDate.getFullYear()) * 12 +
        (endDateValue.getMonth() - startDate.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDateValue.getFullYear() - startDate.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const onSelectStartDate = (date: Date) => {
    setStartDate(date);
    const diff = startDateChange(date);
    onChangePeriod(Number(diff));
    // changeTimeInput(timescale, diff, date, endDate);
  };

  const onSelectEndDate = (date: Date) => {
    setEndDate(date);
    const diff = endDateChange(date);
    onChangePeriod(Number(diff));
    // changeTimeInput(timescale, diff, startDate, date);
  };

  const onChangeFiscalYear = (month: string) => {
    setFyStartMonth(month);
    // if (modelId && startDate && endDate) {
    //   const requestObject = {
    //     model_id: Number(modelId),
    //     request_body: {
    //       time_granularity: timescale,
    //       plan_duration_periods: Number(periodAmount),
    //       ending_period:
    //         timescale.toLowerCase() === 'y'
    //           ? dayjs(endDate).format('YYYY')
    //           : dayjs(endDate).format('DD MMM YY'),
    //       starting_period:
    //         timescale.toLowerCase() === 'y'
    //           ? dayjs(startDate).format('YYYY')
    //           : dayjs(startDate).format('DD MMM YY'),
    //       fy_start_month: `${month}`,
    //     },
    //   };
    //   dispatch(UpdateModelTime({ requestObject, toaster: true }));
    // }
  };

  const handleSubmit = async (form: any) => {
    if (!modelId) return;
    if (!startDate || !endDate) {
      toast({
        title: 'Start and end dates are required',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const tp = timeProperties as Record<string, string | undefined> | undefined;
    const iconValue = typeof form.icon === 'string' ? form.icon.trim() : '';
    const iconPayload = bannerImage || iconValue || (modelData as { icon?: string })?.icon || '';
    const requestBody = {
      model_payload: {
        name: form.name,
        description: form.description ?? '',
        reporting_currency: selectedCurrency.value,
        icon: iconPayload,
      },
      time_payload: {
        calendar_type: tp?.calendar_type ?? 'CALENDAR',
        time_granularity: timescale,
        plan_duration_periods: String(periodAmount),
        starting_period:
          timescale === 'Y' ? dayjs(startDate).format('YYYY') : dayjs(startDate).format('MMM-YY'),
        ending_period:
          timescale === 'Y' ? dayjs(endDate).format('YYYY') : dayjs(endDate).format('MMM-YY'),
        date_format: tp?.date_format ?? '%b-%y',
        fy_start_month: fyStartMonthToApiAbbrev(fyStartMonth),
      },
    };

    setIsSaving(true);
    try {
      await dispatch(
        UpdateModelProperties({
          modelId: Number(modelId),
          request_body: requestBody,
          toaster: true,
        }),
      ).unwrap();
      dispatch(FetchModelBlocks({ modelId: Number(modelId) }));
      dispatch(GetUserModels({}));

      const submitData = {
        ...form,
        bannerImage,
        startDate,
        endDate,
        currency: selectedCurrency.value,
        time_granularity: timescale,
        plan_duration_periods: Number(periodAmount),
        fy_start_month: fyStartMonth,
      };
      if (onSave) {
        onSave(submitData);
      }
      onClose();
    } catch {
      // Errors are surfaced via toast middleware
    } finally {
      setIsSaving(false);
    }
  };

  const handleBannerFileDrop = async (
    acceptedFiles: File[],
    setFieldValue?: (field: string, value: string) => void,
  ) => {
    if (acceptedFiles.length > 0) {
      setBannerLoader(true);
      const file = acceptedFiles[0];
      const data = await handleFileUpload(file, 'blox-user-assets');
      setBannerImage(data);
      setFieldValue?.('icon', data);
      setBannerLoader(false);
    }
  };

  const handleBannerDelete = async (setFieldValue?: (field: string, value: string) => void) => {
    if (bannerImage) {
      setBannerLoader(true);
      await handleFileDelete(bannerImage, 'blox-user-assets').then((response: any) => {
        if (!response?.error) {
          setBannerImage(null);
          setFieldValue?.('icon', '');
        }
      });
      setBannerLoader(false);
    }
  };

  const labelProperties = {
    m: 0,
    mb: space.sm,
    color: text.secondary,
    fontSize: fontSize.label,
    fontFamily: font.body,
    fontWeight: 600,
  } as const;

  const fieldProperties = {
    h: '40px',
    bg: neutral.surface,
    border: '1px solid',
    borderColor: neutral.border,
    borderRadius: radius.control,
    fontSize: fontSize.body,
    fontFamily: font.body,
    fontWeight: 500,
    color: text.primary,
    _hover: { borderColor: neutral.borderStrong },
    _focus: { borderColor: accent.solid, boxShadow: `0 0 0 1px ${accent.solid}` },
    _focusVisible: { borderColor: accent.solid, boxShadow: `0 0 0 1px ${accent.solid}` },
  } as const;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isMobile ? 'full' : '2xl'}
      isCentered
      // 'inside' keeps the dialog within the viewport and scrolls the body,
      // so the header and footer stay pinned.
      scrollBehavior='inside'
    >
      {/* The model header sits at z-index 4000 so its nav dropdowns can cover
          the page, which is above Chakra's default modal layer (1400) — so
          the header was painting over the top of this modal. Lift the whole
          modal layer above it rather than lowering the header. */}
      <ModalOverlay bg='blackAlpha.500' zIndex={5000} />
      <ModalContent
        containerProps={{ zIndex: 5000 }}
        w='540px'
        maxW='540px'
        maxH='calc(100vh - 48px)'
        my='24px'
        bg={neutral.surface}
        borderRadius={radius.surface}
        boxShadow={shadow.overlay}
        overflow='hidden'
        p={0}
      >
        <Formik
          key={[modelId, (modelData as { id?: number })?.id, modelData?.name].join('-')}
          enableReinitialize
          initialValues={{
            name: modelData?.name ?? '',
            description: modelData?.description ?? '',
            icon: modelData?.icon ?? '',
          }}
          onSubmit={(form) => handleSubmit(form)}
          validationSchema={UpdateBlockSchema}
        >
          {({ errors, values, touched, setFieldValue }: any) => {
            const iconUrl = typeof values.icon === 'string' ? values.icon.trim() : '';
            const bannerPreviewUrl = bannerImage || iconUrl || null;
            return (
              <Form
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  maxHeight: 'calc(100vh - 48px)',
                }}
              >
                {/* Pinned header */}
                <ModalHeader
                  px={space['3xl']}
                  py={space.xl}
                  borderBottom='1px solid'
                  borderColor={neutral.border}
                  flexShrink={0}
                >
                  <Text
                    color={text.primary}
                    fontSize={fontSize.title}
                    fontFamily={font.display}
                    fontWeight={700}
                  >
                    Model settings
                  </Text>
                </ModalHeader>
                <ModalCloseButton
                  top={space.xl}
                  right={space.xl}
                  color={text.secondary}
                  borderRadius={radius.control}
                  _hover={{ bg: neutral.surfaceHover }}
                />

                {/* Scrolling body */}
                <ModalBody
                  px={space['3xl']}
                  py={space['2xl']}
                  overflowY='auto'
                  flex='1'
                  minH={0}
                  sx={{ scrollbarGutter: 'stable' } as any}
                >
                  <Flex direction='column' gap={space['2xl']}>
                    {/* Read-only meta */}
                    <Flex gap={space.lg} direction={isMobile ? 'column' : 'row'}>
                      {[
                        {
                          label: 'Created',
                          value: formatDate(
                            modelData?.createdAt ||
                              modelData?.createdDate ||
                              modelData?.date_created ||
                              new Date().toISOString(),
                          ),
                        },
                      ].map((item) => (
                        <Flex
                          key={item.label}
                          flex={1}
                          px={space.lg}
                          py={space.md}
                          bg={neutral.surfaceMuted}
                          borderRadius={radius.control}
                          border='1px solid'
                          borderColor={neutral.border}
                          align='center'
                          gap={space.sm}
                        >
                          <Text color={text.secondary} fontSize={fontSize.body} fontWeight={500}>
                            {item.label}
                          </Text>
                          <Text color={text.primary} fontSize={fontSize.body} fontWeight={500}>
                            {item.value}
                          </Text>
                        </Flex>
                      ))}
                      <Flex
                        flex={1}
                        px={space.lg}
                        py={space.md}
                        bg={neutral.surfaceMuted}
                        borderRadius={radius.control}
                        border='1px solid'
                        borderColor={neutral.border}
                        align='center'
                        gap={space.sm}
                      >
                        <Text color={text.secondary} fontSize={fontSize.body} fontWeight={500}>
                          Owner
                        </Text>
                        <Flex align='center' gap={space.sm} minW={0}>
                          {ownerDisplay.image && (
                            <Image src={ownerDisplay.image} boxSize='20px' borderRadius='full' />
                          )}
                          <Text
                            color={text.primary}
                            fontSize={fontSize.body}
                            fontWeight={500}
                            noOfLines={1}
                          >
                            {ownerDisplay.name}
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>

                    {/* Time settings — the one actively-configured region, so
                        it carries the accent tint. */}
                    <Box
                      p={space.lg}
                      bg={accent.subtle}
                      borderRadius={radius.surface}
                      border='1px solid'
                      borderColor={accent.border}
                    >
                      <Flex direction='column' gap={space.xl}>
                        <FormControl>
                          <FormLabel {...labelProperties}>Fiscal year start month</FormLabel>
                          <Menu closeOnSelect matchWidth>
                            {/* @ts-ignore - Chakra union type complexity in tsserver */}
                            <MenuButton
                              as={Button}
                              rightIcon={<ChevronDownIcon color={accent.solid} />}
                              w='100%'
                              px={space.lg}
                              justifyContent='space-between'
                              outline='none'
                              {...fieldProperties}
                              _active={{ bg: neutral.surface }}
                              _focus={{
                                outline: 'none',
                                boxShadow: 'none',
                                borderColor: neutral.border,
                              }}
                              _focusVisible={{
                                outline: 'none',
                                boxShadow: 'none',
                                borderColor: neutral.border,
                              }}
                            >
                              {fyStartMonth}
                            </MenuButton>
                            <MenuList
                              maxH='220px'
                              overflowY='auto'
                              borderRadius={radius.overlay}
                              zIndex={5100}
                            >
                              <MenuOptionGroup value={fyStartMonth} type='radio'>
                                {MONTH_NAMES.map((month: string) => (
                                  <MenuItemOption
                                    key={month}
                                    value={month}
                                    onClick={() => onChangeFiscalYear(month)}
                                  >
                                    {month}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                            </MenuList>
                          </Menu>
                        </FormControl>

                        <Flex gap={space.lg} direction={isMobile ? 'column' : 'row'}>
                          <Box flex={1}>
                            <FormLabel {...labelProperties}>Start date</FormLabel>
                            <ModelSettingsDatePicker
                              clearSection={false}
                              clearDateInput={() => setStartDate(null)}
                              selected={startDate}
                              onChange={onSelectStartDate}
                              timescale={timescale}
                              isDisabled={false}
                              placeholder='Start Date'
                              placeholderLabel='Start Date'
                              hideChevron={true}
                              emptyColor={text.muted}
                              filledColor={text.primary}
                              leftIcon={<DatePickerIcon />}
                              styles={{
                                width: '100%',
                                height: '40px',
                                padding: '0 12px',
                                background: neutral.surface,
                                borderRadius: radius.control,
                                border: `1px solid ${neutral.border}`,
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '8px',
                                display: 'inline-flex',
                              }}
                            />
                          </Box>
                          <Box flex={1}>
                            <FormLabel {...labelProperties}>End date</FormLabel>
                            <ModelSettingsDatePicker
                              clearSection={false}
                              clearDateInput={() => setEndDate(null)}
                              selected={endDate}
                              onChange={onSelectEndDate}
                              timescale={timescale}
                              isDisabled={false}
                              placeholder='End Date'
                              placeholderLabel='End Date'
                              hideChevron={true}
                              emptyColor={text.muted}
                              filledColor={text.primary}
                              leftIcon={<DatePickerIcon />}
                              minDate={startDate || undefined}
                              styles={{
                                width: '100%',
                                height: '40px',
                                padding: '0 12px',
                                background: neutral.surface,
                                borderRadius: radius.control,
                                border: `1px solid ${neutral.border}`,
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '8px',
                                display: 'inline-flex',
                              }}
                            />
                          </Box>
                        </Flex>
                        <Flex
                          align='center'
                          gap={space.sm}
                          color={text.secondary}
                          fontSize={fontSize.label}
                        >
                          <BsArrowRight />
                          <Text>
                            {timescale === 'M'
                              ? `${Number(periodAmount) + 1} months`
                              : `${Number(periodAmount) + 1} years`}
                          </Text>
                        </Flex>
                      </Flex>
                    </Box>

                    {/* Banner image */}
                    <FormControl>
                      <Flex justify='space-between' align='center' mb={space.md}>
                        <Text color={text.primary} fontSize={fontSize.body} fontWeight={600}>
                          Banner image
                        </Text>
                        <Text color={text.muted} fontSize={fontSize.label} fontWeight={400}>
                          Recommended size: 1080x1920px.
                        </Text>
                      </Flex>
                      <Box position='relative'>
                        {bannerLoader && (
                          <FullScreenLoader
                            bgColor='linear-gradient(#FFFFFF, #F3F3F3)'
                            style={{ position: 'absolute', borderRadius: radius.control }}
                            height='114px'
                            width='100%'
                          />
                        )}
                        <Dropzone
                          onDrop={(acceptedFiles) =>
                            handleBannerFileDrop(acceptedFiles, setFieldValue)
                          }
                          accept={acceptedFormats}
                          maxSize={5 * 1024 * 1024}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <Box
                              {...getRootProps()}
                              border='1px dashed'
                              borderColor={neutral.border}
                              borderRadius={radius.control}
                              py={space.md}
                              px={space.xl}
                              height='114px'
                              overflow='hidden'
                              bg={neutral.surfaceMuted}
                              cursor='pointer'
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                              flexDirection='column'
                              gap={space.md}
                              transition='border-color 0.12s ease, background 0.12s ease'
                              _hover={{ borderColor: accent.solid, bg: accent.subtle }}
                            >
                              <input {...getInputProps()} />
                              {bannerPreviewUrl ? (
                                <Flex direction='column' align='center' gap={space.md}>
                                  <Image
                                    src={bannerPreviewUrl}
                                    maxH='44px'
                                    borderRadius={radius.control}
                                    alt='Banner or model icon preview'
                                  />
                                  <Button
                                    size='sm'
                                    variant='outline'
                                    h='28px'
                                    borderColor={neutral.border}
                                    borderRadius={radius.control}
                                    color={text.primary}
                                    fontSize={fontSize.label}
                                    fontWeight={500}
                                    _hover={{ bg: neutral.surfaceHover }}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (bannerImage) {
                                        handleBannerDelete(setFieldValue);
                                      } else if (iconUrl) {
                                        setFieldValue('icon', '');
                                      }
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </Flex>
                              ) : (
                                <>
                                  <Text
                                    color={accent.solid}
                                    fontSize={fontSize.label}
                                    fontWeight={600}
                                    lineHeight='18px'
                                  >
                                    Click to upload
                                    <Text as='span' color={text.secondary} fontWeight={500} ml={1}>
                                      or drag and drop
                                    </Text>
                                  </Text>
                                  <Text
                                    color={text.muted}
                                    fontSize={fontSize.label}
                                    fontWeight={400}
                                    lineHeight='18px'
                                  >
                                    JPG, JPEG, PNG less than 5MB
                                  </Text>
                                </>
                              )}
                            </Box>
                          )}
                        </Dropzone>
                      </Box>
                    </FormControl>

                    {/* Name + currency */}
                    <Flex gap={space.lg} direction={isMobile ? 'column' : 'row'}>
                      <FormControl flex={1}>
                        <FormLabel {...labelProperties}>Model name</FormLabel>
                        <Input
                          value={values.name}
                          onChange={(event) => setFieldValue('name', event.target.value)}
                          placeholder='Enter model name'
                          px={space.lg}
                          {...fieldProperties}
                          _placeholder={{ color: text.muted }}
                        />
                        {touched.name && errors.name && (
                          <Text color='red.500' fontSize={fontSize.label} mt={1}>
                            {errors.name}
                          </Text>
                        )}
                      </FormControl>
                      <FormControl flex={1}>
                        <FormLabel {...labelProperties}>Currency</FormLabel>
                        <Menu strategy='fixed' matchWidth>
                          {/* @ts-ignore - Chakra union type complexity in tsserver */}
                          <MenuButton
                            as={Button}
                            rightIcon={<ChevronDownIcon color={accent.solid} />}
                            w='100%'
                            px={space.lg}
                            textAlign='left'
                            justifyContent='space-between'
                            outline='none'
                            {...fieldProperties}
                            _active={{ bg: neutral.surface }}
                            _focus={{
                              outline: 'none',
                              boxShadow: 'none',
                              borderColor: neutral.border,
                            }}
                            _focusVisible={{
                              outline: 'none',
                              boxShadow: 'none',
                              borderColor: neutral.border,
                            }}
                          >
                            {selectedCurrency ? selectedCurrency.label : 'Select currency'}
                          </MenuButton>
                          <Portal>
                            <MenuList
                              borderRadius={radius.overlay}
                              key='currency-options-menu-list'
                              maxH='300px'
                              overflowY='auto'
                              overscrollBehavior='contain'
                              zIndex={5100}
                            >
                              {CURRENCY_OPTIONS.map((currency) => (
                                <MenuItem
                                  key={`${currency.value}-${currency.label}`}
                                  onClick={() => setSelectedCurrency(currency)}
                                >
                                  {currency.label}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Portal>
                        </Menu>
                      </FormControl>
                    </Flex>

                    {/* Description */}
                    <FormControl>
                      <FormLabel {...labelProperties}>Description</FormLabel>
                      <Textarea
                        value={values.description}
                        onChange={(event) => setFieldValue('description', event.target.value)}
                        placeholder='Enter description, 100 words max'
                        minH='72px'
                        p={space.lg}
                        border='1px solid'
                        borderColor={neutral.border}
                        borderRadius={radius.control}
                        fontSize={fontSize.body}
                        fontFamily={font.body}
                        fontWeight={500}
                        color={text.primary}
                        resize='none'
                        _placeholder={{ color: text.muted }}
                        _hover={{ borderColor: neutral.borderStrong }}
                        _focus={{
                          borderColor: accent.solid,
                          boxShadow: `0 0 0 1px ${accent.solid}`,
                        }}
                        _focusVisible={{
                          borderColor: accent.solid,
                          boxShadow: `0 0 0 1px ${accent.solid}`,
                        }}
                      />
                      {touched.description && errors.description && (
                        <Text color='red.500' fontSize={fontSize.label} mt={1}>
                          {errors.description}
                        </Text>
                      )}
                    </FormControl>
                  </Flex>
                </ModalBody>

                {/* Pinned footer */}
                <ModalFooter
                  px={space['3xl']}
                  py={space.xl}
                  borderTop='1px solid'
                  borderColor={neutral.border}
                  gap={space.lg}
                  flexShrink={0}
                >
                  <Button
                    flex={1}
                    h='40px'
                    bg={neutral.surface}
                    borderRadius={radius.control}
                    border='1px solid'
                    borderColor={neutral.border}
                    color={text.primary}
                    fontSize={fontSize.body}
                    fontFamily={font.body}
                    fontWeight={500}
                    _hover={{ bg: neutral.surfaceHover }}
                    onClick={onClose}
                    type='button'
                  >
                    Cancel
                  </Button>
                  <Button
                    flex={1}
                    h='40px'
                    bg={accent.solid}
                    borderRadius={radius.control}
                    color={text.inverse}
                    fontSize={fontSize.body}
                    fontFamily={font.body}
                    fontWeight={600}
                    _hover={{ bg: accent.hover }}
                    _active={{ bg: accent.hover }}
                    type='submit'
                    isDisabled={bannerLoader || isSaving}
                    isLoading={isSaving}
                  >
                    Save changes
                  </Button>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default ModelSettingsModal;
